import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { App, Button, Drawer, Input, Typography } from 'antd';
import { Copy, Trash2, Pencil, Plus, Search } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { BuilderTable, type BuilderTableColumn } from '@/components/BuilderTable';
import { BuilderChip } from '@/components/BuilderChip';
import { BuilderIconButton } from '@/components/BuilderIconButton';
import { DrawerHeader } from '@/components/DrawerHeader';
import { useBreakpoint, useDrawerSurface } from '@/hooks';
import { HtsQuestionnaireDetailSheet } from './HtsQuestionnaireDetailSheet';
import { AdminHtsQuestionnairesService, BaseAPIClient } from '@/services/api';
import type { HtsQuestionnaire } from '@/services/api';
import { formatDate } from '@/utils/formatters';
import { ROUTES, buildRoute } from '@/routes';
import { containerVariants, itemVariants } from '@/utils';
import styles from './AdminHtsQuestionnaires.module.scss';

const { Title, Text } = Typography;

const GRID_TEMPLATE_COLUMNS =
  'minmax(220px, 2.5fr) minmax(100px, 0.8fr) minmax(90px, 0.9fr) minmax(110px, 0.85fr) 140px';

const PER_PAGE = 20;

export const HtsQuestionnaireList: React.FC = () => {
  const { getToken } = useAuth();
  const { notification, modal } = App.useApp();
  const navigate = useNavigate();

  const [questionnaires, setQuestionnaires] = useState<HtsQuestionnaire[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewing, setViewing] = useState<HtsQuestionnaire | null>(null);
  const { isMobile } = useBreakpoint();
  const drawerSurface = useDrawerSurface();

  const getService = useCallback(async () => {
    const token = await getToken();
    if (!token) throw new Error('Not authenticated');
    return new AdminHtsQuestionnairesService(token);
  }, [getToken]);

  const fetchQuestionnaires = useCallback(async () => {
    setLoading(true);
    try {
      const svc = await getService();
      setQuestionnaires(await svc.list());
    } catch (err) {
      notification.error({ message: BaseAPIClient.parseErrorMsg(err) });
    } finally {
      setLoading(false);
    }
  }, [getService, notification]);

  useEffect(() => {
    void fetchQuestionnaires();
  }, [fetchQuestionnaires]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return questionnaires.filter((q) => {
      if (!term) return true;
      return q.name.toLowerCase().includes(term);
    });
  }, [questionnaires, search]);

  const openEditor = (id: string) =>
    navigate(buildRoute(ROUTES.ADMIN_HTS_QUESTIONNAIRE_EDIT, { id }));

  const handleDuplicate = async (q: HtsQuestionnaire) => {
    try {
      const svc = await getService();
      await svc.create({
        name: `${q.name} (Copy)`,
        description: q.description ?? undefined,
        questions: q.questions.map((question) => ({
          tempId: question.id,
          section: question.section,
          order: question.order,
          inputType: question.inputType,
          text: question.text,
          helpText: question.helpText ?? undefined,
          options: question.options,
          isSensitive: question.isSensitive,
          classificationTopic: question.classificationTopic,
          readinessPillars: question.readinessPillars,
          hardGateRule: question.hardGateRule,
          groupFields: question.groupFields,
        })),
      });
      notification.success({ message: 'HTS questionnaire duplicated' });
      await fetchQuestionnaires();
    } catch (err) {
      notification.error({ message: BaseAPIClient.parseErrorMsg(err) });
    }
  };

  const handleDelete = async (q: HtsQuestionnaire) => {
    try {
      const svc = await getService();
      await svc.remove(q.id);
      notification.success({ message: 'HTS questionnaire deleted' });
      await fetchQuestionnaires();
    } catch (err) {
      notification.error({ message: BaseAPIClient.parseErrorMsg(err) });
    }
  };

  const goToQuestionSet = (id: string) => navigate(buildRoute(ROUTES.ADMIN_QUESTION_SET_EDIT, { id }));

  const confirmDelete = (q: HtsQuestionnaire) => {
    const inUse = q.questionSets.length > 0;
    const instance = modal.confirm({
      title: inUse ? 'This questionnaire is still in use' : 'Delete this HTS questionnaire?',
      icon: null,
      maskClosable: true,
      centered: true,
      content: inUse ? (
        <div className={styles.deleteHint}>
          <div>Unassign it from these question sets before it can be deleted:</div>
          <div className={styles.questionSetRefs}>
            {q.questionSets.map((qs) => (
              <BuilderChip
                key={qs.id}
                tint="purple"
                onClick={() => {
                  instance.destroy();
                  goToQuestionSet(qs.id);
                }}
              >
                {qs.name}
              </BuilderChip>
            ))}
          </div>
        </div>
      ) : (
        'This cannot be undone.'
      ),
      okText: 'Delete',
      okButtonProps: { danger: true, disabled: inUse },
      onOk: () => handleDelete(q),
    });
  };

  const columns: BuilderTableColumn<HtsQuestionnaire>[] = [
    {
      key: 'name',
      title: 'HTS Questionnaire',
      sortFn: (a, b) => a.name.localeCompare(b.name),
      render: (q) => <span className={styles.cellName}>{q.name}</span>,
    },
    {
      key: 'questions',
      title: 'Questions',
      sortFn: (a, b) => a.questions.length - b.questions.length,
      render: (q) => (
        <span className={styles.cellMuted}>
          {q.questions.length} question{q.questions.length !== 1 ? 's' : ''}
        </span>
      ),
    },
    {
      key: 'usedIn',
      title: 'Used in',
      sortFn: (a, b) => a.questionSets.length - b.questionSets.length,
      render: (q) =>
        q.questionSets.length > 0 ? (
          <span className={styles.cellMuted}>
            {q.questionSets.length} question set{q.questionSets.length !== 1 ? 's' : ''}
          </span>
        ) : (
          <span className={styles.cellMuted}>—</span>
        ),
    },
    {
      key: 'updated',
      title: 'Updated',
      sortFn: (a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
      render: (q) => <span className={styles.cellDate}>{formatDate(q.updatedAt)}</span>,
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (q) => (
        <div className={styles.rowActions} onClick={(e) => e.stopPropagation()}>
          <BuilderIconButton icon={<Pencil style={{ width: '1em', height: '1em' }} />} title="Edit" onClick={() => openEditor(q.id)} />
          <BuilderIconButton icon={<Copy style={{ width: '1em', height: '1em' }} />} title="Duplicate" onClick={() => handleDuplicate(q)} />
          <BuilderIconButton
            icon={<Trash2 style={{ width: '1em', height: '1em' }} />}
            title="Delete"
            danger
            onClick={() => confirmDelete(q)}
          />
        </div>
      ),
    },
  ];

  return (
    <motion.div className={styles.page} variants={containerVariants} initial="hidden" animate="visible">
      <motion.div className={styles.pageHead} variants={itemVariants}>
        <div>
          <Title level={2} className={styles.pageTitle}>
            HTS Questionnaires
          </Title>
          <Text className={styles.pageSub}>
            High-Trust Suitability questionnaire definitions. Attach one to a question set to
            require it before an interview.
          </Text>
        </div>
        <Button
          type="primary"
          icon={<Plus style={{ width: '1em', height: '1em' }} />}
          className={styles.btnPrimary}
          onClick={() => navigate(ROUTES.ADMIN_HTS_QUESTIONNAIRE_NEW)}
        >
          Add HTS Questionnaire
        </Button>
      </motion.div>

      <motion.div className={styles.toolbar} variants={itemVariants}>
        <div className={styles.search}>
          <Search className={styles.searchIcon} style={{ width: '1em', height: '1em' }} />
          <Input
            placeholder="Search questionnaires…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      </motion.div>

      <motion.div variants={itemVariants}>
      <BuilderTable<HtsQuestionnaire>
        columns={columns}
        gridTemplateColumns={GRID_TEMPLATE_COLUMNS}
        data={filtered}
        rowKey={(q) => q.id}
        pageSize={PER_PAGE}
        loading={loading}
        onRowClick={(q) => (isMobile ? setViewing(q) : openEditor(q.id))}
        mobileActionsLabel={(q) => `Actions for ${q.name}`}
        onMobileDetails={(q) => setViewing(q)}
        emptyTitle={questionnaires.length ? 'No questionnaires match' : 'No HTS questionnaires yet'}
        emptySub={
          questionnaires.length
            ? 'Adjust the search.'
            : 'Use "Add HTS Questionnaire" to author your first one.'
        }
      />
      </motion.div>

      <Drawer
        title={<DrawerHeader title="HTS Questionnaire" onClose={() => setViewing(null)} />}
        closable={false}
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        {...drawerSurface}
        destroyOnHidden
        className={styles.drawer}
      >
        {viewing && (
          <HtsQuestionnaireDetailSheet
            questionnaire={viewing}
            onEdit={() => openEditor(viewing.id)}
            onDuplicate={() => {
              setViewing(null);
              void handleDuplicate(viewing);
            }}
            onDelete={() => {
              setViewing(null);
              confirmDelete(viewing);
            }}
            onNavigateToQuestionSet={(id) => {
              setViewing(null);
              goToQuestionSet(id);
            }}
          />
        )}
      </Drawer>
    </motion.div>
  );
};

export default HtsQuestionnaireList;
